import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type SignatureCanvasType from 'react-signature-canvas';
import { motion, AnimatePresence, useReducedMotion, Variants } from 'framer-motion';
import type { FormPackageV2Draft, FormPackageV2SubmitPayload, FormPayer, InsuranceCheckOutcome, InsuranceUnavailableReason, ApiError, IntakeFormField, IntakeFormSection, PolicySlot, PracticeInfo, SignatureField } from '../../api/bookingApi';
import { checkFormPackageV2Insurance, getFormPackageV2Draft, saveFormPackageV2Draft, searchFormPackageV2Payers } from '../../api/bookingApi';
import { type UploadMeta } from './FileUploadField';
import { V2ProgressHeader, V2FooterBar } from './V2Chrome';
import { birthdayPartsFromISO, isBirthdayField, parseBirthday, type BirthdayParts } from '../custom-form/birthday';
import { getLocalToday } from '../../utils/date';
import { isCanvasEmpty } from './canvasPng';
import { buildV2SubmitPayload, payerCompanion, V2AssemblyError } from './buildV2SubmitPayload';
import { useV2Uploads, V2UploadError, uploadErrorMessage } from './useV2Uploads';
import { attachCanvasPreserve } from './useCanvasPreserve';
import { buildReviewModel } from './reviewModel';
import { isFieldVisible } from './fieldVisibility';
import { isFieldRequired } from './fieldRequiredness';
import {
  INSURANCE_CHECK_WAIT_MS,
  canSendCheck,
  coverageConfirmedText,
  findInsuranceSlots,
  fixableMessage,
  isFixableOutcome,
  isTemporaryFailure,
  unavailableNotice,
  planSlotCheck,
  recordCheckResult,
  recordCheckSent,
  screenTouchesSlot,
  submittableInsuranceChecks,
  type FieldPlacement,
  type FieldRef,
  type InsuranceLedger,
  type SlotCheckPlan,
} from './insuranceCheck';
import { CoverageConfirmedNote } from './V2Bits';
import { exactPayerMatch, payerQuery, type PayerConfirmView } from './PayerTypeahead';
import { slotKeys, type FileUploadFieldKey, type V2Item, type V2Package } from './types';
import { V2WelcomeStep } from './steps/V2WelcomeStep';
import { V2AgreementReviewStep } from './steps/V2AgreementReviewStep';
import { V2AgreementSigStep } from './steps/V2AgreementSigStep';
import { V2SectionStep } from './steps/V2SectionStep';
import { V2ConsentStep } from './steps/V2ConsentStep';
import { V2ReviewStep } from './steps/V2ReviewStep';

// The form_package_v2 step renderer (backend#2049). Walk: welcome → per
// agreement (review + agree, then its signature fields one per screen) → the
// form fields grouped one SECTION per screen (booking-frontend#20) → consent →
// review. State is keyed by instance item id, every signature or file is staged
// as an upload the moment its screen is confirmed, and the submit document
// references those uploads by id. Nothing is ever posted as base64.
//
// This component owns ALL flow state (answers, refs, validation, staging,
// submission); the per-step screens under ./steps/ are purely presentational.
//
// Insurance (booking-frontend#46): a payer field's pick also stores the payer's
// stedi_id under its `payer_id_key` in the same item's answers, and Next from a
// screen holding a policy's details runs one live check with the DOB proof
// (see insuranceCheck.ts). The patient can always continue.
//
// Confirming the insurer (booking-frontend#51): text in a payer field that is not
// a pick carries no payer ID, so no check runs. Such text is searched: a card
// upload's autofill at once, anything else (typed, resumed from a draft) at Next,
// and every screen's at submit. A card-read name with one exact match is picked;
// other matches ask under the field, and Next and submit wait until the patient
// chooses one or says it isn't listed. A card read never replaces a pick.

type StepType = 'welcome' | 'agreement_review' | 'agreement_sig_field' | 'section' | 'consent' | 'review';

interface Step {
  type: StepType;
  id: string;
  item?: V2Item;
  sigField?: SignatureField;
  sigFieldIndex?: number;
  section?: IntakeFormSection;
  /** The section's renderable fields (a template agreement's static_text is dropped — its paragraphs were shown on the review step). */
  fields?: IntakeFormField[];
  sectionIndex?: number;
  sectionCount?: number;
  /** When a long section is split across screens: this screen's part number and the section's part count. */
  partIndex?: number;
  partCount?: number;
  /** Journey stage this screen belongs to, for the header ("Medical history · 2 of 4").
   *  `stageKey` groups consecutive screens; `stageLabel` is patient-visible; `stagePos`/`stageSize`
   *  are this screen's place within the stage (filled by a second pass over the built steps). */
  stageKey?: string;
  stageLabel?: string;
  stagePos?: number;
  stageSize?: number;
}

// A phone screen holds this many fields comfortably; longer sections are split
// into parts (booking-frontend#28). A follow-up question never starts a new
// part — it stays on the screen with the question it belongs to, so a part can
// run slightly over.
const MAX_FIELDS_PER_SCREEN = 8;
const FOLLOW_UP_LABEL = /^\s*(if (so|yes|applicable)\b|please (explain|list|describe|specify)\b)/i;

/** Bring a section field into view and put focus on its control (keyboard and
 *  screen-reader users land on it; canvases and file pickers have no focusable control),
 *  or on `target` inside it. */
function focusField(itemId: string, key: string, target = 'input, select, textarea') {
  const wrapper = document.getElementById(`pkgfield-${itemId}-${key}`);
  wrapper?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  wrapper?.querySelector<HTMLElement>(target)?.focus({ preventScroll: true });
}

// A payer field's ask: focus goes to the ask, not a choice in it, so a stray
// Enter cannot pick a payer.
const PAYER_ASK = '[data-payer-confirm]';
// A payer search still out when Next or submit stops waiting for it.
const LATE = 'late' as const;

/** Each promise's result, or `late` for one still pending after `ms`. */
async function settleWithin<T, L>(promises: Promise<T>[], ms: number, late: L): Promise<(T | L)[]> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const deadline = new Promise<L>((resolve) => { timer = setTimeout(() => resolve(late), ms); });
  try {
    return await Promise.all(promises.map((p) => Promise.race([p, deadline])));
  } finally {
    clearTimeout(timer);
  }
}

function chunkFields(fields: IntakeFormField[], max: number): IntakeFormField[][] {
  const parts: IntakeFormField[][] = [];
  let current: IntakeFormField[] = [];
  for (const field of fields) {
    const isFollowUp = current.length > 0 && FOLLOW_UP_LABEL.test(field.label ?? '');
    if (current.length >= max && !isFollowUp) {
      parts.push(current);
      current = [];
    }
    current.push(field);
  }
  if (current.length) parts.push(current);
  return parts;
}

interface FormPackageV2RendererProps {
  pkg: V2Package;
  clientId: string;
  token: string;
  onSubmit: (payload: FormPackageV2SubmitPayload) => Promise<void>;
  loading: boolean;
  /** An upload told us the link itself is dead (401 / 404 / 410); the page decides the screen. */
  onLinkInvalid?: (err: V2UploadError) => void;
  /** Compact branding for the sticky mobile header (Shell's big logo is hidden there). */
  practiceInfo?: PracticeInfo | null;
  /** Bearer for the live insurance check, from this tab's DOB verify. Memory only;
   *  null (cached-token resume, kiosk, no DOB on file) means no check is ever sent. */
  dobProofToken: string | null;
  /** This package's insurance-check attempts. Held by the page so a remount (the
   *  re-sign recovery) cannot reset the count; never stored or drafted. */
  insuranceLedger: InsuranceLedger;
}

/** An advisory insurance outcome showing on a screen. While the slot's answers still
 *  match `fingerprint`, the primary button offers "Continue anyway". */
interface InsuranceNotice {
  slot: PolicySlot;
  stepId: string;
  fingerprint: string;
  /** Footer copy; null when the message sits on a field of the screen instead. */
  text: string | null;
  temporary?: boolean;
  retry?: boolean;
}

/** A payer field whose unpicked text needs a look, `id` being `${instanceItemId}:${key}`. */
interface PayerFieldRef {
  id: string;
  itemId: string;
  key: string;
  text: string;
}

const PAYER_CONFIRM_NOTICE = 'Choose your insurance company from the list, or "My insurance isn\'t listed".';
// Next and submit wait this long for a payer search. A slower answer still asks
// under its field, and submit comes back to it.
const PAYER_SEARCH_WAIT_MS = 5000;

export default function FormPackageV2Renderer({ pkg, clientId, token, onSubmit, loading, onLinkInvalid, practiceInfo, dobProofToken, insuranceLedger }: FormPackageV2RendererProps) {
  const items = useMemo(() => pkg.items.slice().sort((a, b) => a.ordinal - b.ordinal), [pkg]);
  const uploads = useV2Uploads({ clientId, token, packageInstanceId: pkg.packageInstanceId });

  // Agreement state (keyed by instance item id)
  const [agreedItemIds, setAgreedItemIds] = useState<string[]>([]);
  const [agreementFieldValues, setAgreementFieldValues] = useState<Record<string, Record<number, string>>>({});

  // Intake/custom values (keyed by instance item id, then field key)
  // Unfinished birthdays stay in memory; drafts and checks read only formValues.
  const [birthdayParts, setBirthdayParts] = useState<Record<string, BirthdayParts>>({});
  const birthdayPartsRef = useRef(birthdayParts);
  birthdayPartsRef.current = birthdayParts;
  const [formValues, setFormValues] = useState<Record<string, Record<string, unknown>>>({});
  const [uploadMetaByItem, setUploadMetaByItem] = useState<Record<string, Record<string, UploadMeta>>>({});

  // Consent
  const [consentName, setConsentName] = useState('');

  // Navigation / status
  const [storedStepIndex, setCurrentStepIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  // Test hook: the id of the step whose enter animation has completed. The
  // mounted wrapper exposes it as data-settled so the e2e walk can wait for
  // animation-complete without probing computed styles.
  const [settledStepId, setSettledStepId] = useState<string | null>(null);
  // Set when the patient taps Edit on the Final Review: the next successful
  // Next ("Save & return") jumps straight back to the review instead of
  // walking forward; Back leaves the mode.
  const [editingFromReview, setEditingFromReview] = useState(false);
  const [error, setError] = useState('');
  // Per-field messages on a section screen, keyed `${instanceItemId}:${fieldKey}`.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [staging, setStaging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Draft resume: block autosave until the initial load has run, so an empty
  // starting state never overwrites a server draft before we've read it.
  const [hydrating, setHydrating] = useState(true);
  // JSON of the last snapshot we persisted, so an unchanged flow is not re-PUT.
  const lastSavedRef = useRef<string | null>(null);

  // Insurance. None of this is saved in the draft; the companion answers are
  // ordinary formValues, and the attempt ledger (with each slot's last check_id)
  // comes from the page.
  const [payerFallback, setPayerFallback] = useState(false);
  const [checking, setChecking] = useState(false);
  const [fieldHighlights, setFieldHighlights] = useState<Record<string, string>>({});
  const [insuranceNotices, setInsuranceNotices] = useState<InsuranceNotice[]>([]);
  const [coverageNote, setCoverageNote] = useState<{ stepId: string; lines: string[] } | null>(null);
  // "Coverage confirmed" lines waiting for the next advance (a screen that stays
  // for another slot's fixable outcome shows them once the patient moves on).
  const pendingConfirmationsRef = useRef<string[]>([]);
  // Payer asks (#51), keyed `${instanceItemId}:${fieldKey}`, and the screen Next or
  // submit stopped on for one (its footer notice).
  const [payerConfirms, setPayerConfirms] = useState<Record<string, PayerConfirmView>>({});
  const [payerHoldStepId, setPayerHoldStepId] = useState<string | null>(null);
  // Per field: text that needs no ask (not listed, or nothing matched), the text a
  // card autofill wrote (an exact match for it is picked), and the latest search
  // (an edit or a newer search drops an older answer).
  const payerSettledRef = useRef<Record<string, string>>({});
  const autofilledRef = useRef<Record<string, string>>({});
  const payerSearchRef = useRef<Record<string, number>>({});
  // Per field: the typeahead's latest answer, so Next need not search that text again;
  // the search in flight, so the same text is not searched twice at once; and
  // whether the pick came from a card read (a later card may replace it, unlike
  // the patient's own pick).
  const payerResultsRef = useRef<Record<string, { query: string; payers: FormPayer[] }>>({});
  const payerInflightRef = useRef<Record<string, { seq: number; text: string; promise: Promise<PayerConfirmView | null> }>>({});
  const cardPickedRef = useRef<Record<string, true>>({});
  // Set the moment payer search falls back, for answers already on their way.
  const payerFallbackRef = useRef(false);
  // An ask to focus once its screen has settled (submit goes back to it).
  const payerFocusRef = useRef<{ stepId: string; itemId: string; key: string } | null>(null);
  // An autofill applies after its upload, in the closure of the render that picked the file.
  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;

  // Canvas refs
  const agreementSigRefs = useRef<Map<string, SignatureCanvasType | null>>(new Map());
  const intakeSigRefs = useRef<Map<string, SignatureCanvasType | null>>(new Map());
  const finalSigRef = useRef<SignatureCanvasType>(null);

  // The footer CTA persists across step transitions, so a tap during the exit
  // animation could act on a half-unmounted step. Locked on advance, released
  // by the entering step's onAnimationComplete.
  const navLockRef = useRef(false);
  const reduceMotion = useReducedMotion();

  // Built once per instance and replayed verbatim on retry; any edit drops it.
  const payloadRef = useRef<FormPackageV2SubmitPayload | null>(null);
  const invalidate = () => { payloadRef.current = null; };

  // The final authorization exists exactly when an agreement has been accepted
  // (a required agreement must be accepted to get past its step, so this also covers
  // it). backend#1998 rejects a consent with nothing to authorize and requires one
  // whenever an agreement is accepted, so the step and the payload follow this flag.
  const consentRequired = pkg.hasAgreements && agreedItemIds.length > 0;

  const templateFieldsByItem = useMemo(() => {
    const out: Record<string, Record<string, IntakeFormField>> = {};
    items.forEach((item) => {
      if (item.kind === 'agreement' && item.mode !== 'template') return;
      const keys: Record<string, IntakeFormField> = {};
      item.sections.forEach((section) => section.fields.forEach((field) => { keys[field.key] = field; }));
      out[item.instanceItemId] = keys;
    });
    return out;
  }, [items]);

  // itemId → payer field key → the companion answer key it declares.
  const companionKeysByItem = useMemo(() => {
    const out: Record<string, Record<string, string>> = {};
    items.forEach((item) => item.sections.forEach((section) => section.fields.forEach((field) => {
      if (field.payer_id_key) (out[item.instanceItemId] ??= {})[field.key] = field.payer_id_key;
    })));
    return out;
  }, [items]);
  const insuranceSlots = useMemo(() => findInsuranceSlots(items), [items]);

  // Build steps
  const steps = useMemo(() => {
    const s: Step[] = [{ type: 'welcome', id: 'welcome', stageKey: 'welcome', stageLabel: 'Welcome' }];
    for (const item of items) {
      if (item.kind === 'agreement') {
        // All agreement screens (each agreement's review + its signature fields) form
        // one "Agreements" stage, so the header names the stage rather than each doc.
        s.push({ type: 'agreement_review', item, id: `agree-${item.instanceItemId}`, stageKey: 'agreements', stageLabel: 'Agreements' });
        item.signatureFields.forEach((sf, idx) => {
          s.push({ type: 'agreement_sig_field', item, sigField: sf, sigFieldIndex: idx, id: `agsig-${item.instanceItemId}-${idx}`, stageKey: 'agreements', stageLabel: 'Agreements' });
        });
      }
      // The item's fields, grouped one section per screen (booking-frontend#20).
      // A form-builder agreement's static_text paragraphs were already shown on
      // its review step; an intake/custom form keeps them inline, read-only.
      const sectionSteps: Step[] = [];
      item.sections.forEach((section, idx) => {
        const fields = item.kind === 'agreement'
          ? section.fields.filter((f) => f.field_type !== 'static_text')
          : section.fields;
        if (fields.length === 0) return;
        const parts = chunkFields(fields, MAX_FIELDS_PER_SCREEN);
        parts.forEach((partFields, p) => {
          sectionSteps.push({
            type: 'section',
            item,
            section,
            fields: partFields,
            id: `sec-${item.instanceItemId}-${idx}-${p}`,
            partIndex: p + 1,
            partCount: parts.length,
            // Each section is its own stage; a section split into parts stays one
            // stage, so the parts become its "X of Y". Keyed by item+section so two
            // forms sharing a section label ("Medical history") stay separate.
            stageKey: `sec:${item.instanceItemId}:${idx}`,
            stageLabel: section.label || item.name,
          });
        });
      });
      sectionSteps.forEach((step, i) => {
        step.sectionIndex = i + 1;
        step.sectionCount = sectionSteps.length;
      });
      s.push(...sectionSteps);
    }
    // The consent step is part of the walk whenever the package has agreements,
    // so the step total never jumps after the patient accepts one; when nothing
    // was accepted it is a plain "no authorization needed" screen.
    if (pkg.hasAgreements) s.push({ type: 'consent', id: 'consent', stageKey: 'consent', stageLabel: 'Authorization' });
    s.push({ type: 'review', id: 'review', stageKey: 'review', stageLabel: 'Review' });

    // Second pass: number each screen within its (consecutive) stage.
    for (let i = 0; i < s.length; i++) {
      const stageSteps = s.filter((step) => step.stageKey === s[i].stageKey);
      s[i].stagePos = stageSteps.indexOf(s[i]) + 1;
      s[i].stageSize = stageSteps.length;
    }
    return s;
  }, [items, pkg.hasAgreements]);

  // Preserve canonical step IDs and draft indexes; only the walk and counters
  // depend on answers. Resolve a hidden saved/current index before drawing it.
  const visibleIndexes = steps.flatMap((step, index) => step.type !== 'section'
    || step.fields!.some((field) => isFieldVisible(field, formValues[step.item!.instanceItemId] ?? {}, step.item!.sections))
    ? [index] : []);
  const currentStepIndex = visibleIndexes.find((index) => index >= storedStepIndex) ?? steps.length - 1;
  const visiblePosition = visibleIndexes.indexOf(currentStepIndex);
  const canonicalStep = steps[currentStepIndex];
  const stageParts = visibleIndexes.map((index) => steps[index]).filter((step) => step.stageKey === canonicalStep.stageKey);
  const itemParts = visibleIndexes.map((index) => steps[index]).filter((step) => step.type === 'section' && step.item === canonicalStep.item);
  const currentStep = { ...canonicalStep,
    stagePos: stageParts.indexOf(canonicalStep) + 1, stageSize: stageParts.length,
    partIndex: stageParts.indexOf(canonicalStep) + 1, partCount: stageParts.length,
    sectionIndex: itemParts.indexOf(canonicalStep) + 1, sectionCount: itemParts.length,
  };
  const progress = Math.round(((visiblePosition + 1) / visibleIndexes.length) * 100);
  useEffect(() => {
    if (!hydrating && storedStepIndex !== currentStepIndex) setCurrentStepIndex(currentStepIndex);
  }, [hydrating, storedStepIndex, currentStepIndex]);

  const getFormValues = (itemId: string) => formValues[itemId] ?? {};
  const clearFieldMessages = (itemId: string, key: string) => {
    const id = `${itemId}:${key}`;
    setError('');
    setFieldErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setFieldHighlights((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };
  // An item's answers without the payer ID beside payer field `key`: its text is
  // not a picked payer any more (typed, autofilled, or the search fell back).
  const withoutCompanion = (itemId: string, key: string, values: Record<string, unknown>) => {
    const companion = companionKeysByItem[itemId]?.[key];
    if (!companion || !(companion in values)) return values;
    const next = { ...values };
    delete next[companion];
    return next;
  };
  const dropPayerConfirms = (ids: string[]) => setPayerConfirms((prev) => {
    if (!ids.some((id) => id in prev)) return prev;
    const next = { ...prev };
    ids.forEach((id) => { delete next[id]; });
    return next;
  });
  // A payer field's text changed: a search for the old text, any ask about it, and
  // the card read or card pick it held no longer apply.
  const forgetPayerSearch = (id: string) => {
    payerSearchRef.current[id] = (payerSearchRef.current[id] ?? 0) + 1;
  };
  const forgetPayerText = (itemId: string, key: string) => {
    const id = `${itemId}:${key}`;
    forgetPayerSearch(id);
    delete autofilledRef.current[id];
    delete cardPickedRef.current[id];
    dropPayerConfirms([id]);
  };
  const setFieldValue = (itemId: string, key: string, value: unknown) => {
    invalidate();
    const field = templateFieldsByItem[itemId]?.[key];
    const values = { ...(formValuesRef.current[itemId] ?? {}) };
    if (field && isBirthdayField(field)) {
      const parts = birthdayPartsFromISO(value);
      const nextParts = { ...birthdayPartsRef.current, [`${itemId}:${key}`]: parts };
      birthdayPartsRef.current = nextParts;
      setBirthdayParts(nextParts);
      const parsed = parseBirthday(parts, getLocalToday());
      if (parsed.status === 'valid') values[key] = parsed.iso;
      else delete values[key];
    } else values[key] = value;
    const next = { ...formValuesRef.current, [itemId]: withoutCompanion(itemId, key, values) };
    formValuesRef.current = next;
    setFormValues(next);
    clearFieldMessages(itemId, key);
    if (key === 'insurance_status') {
      const item = items.find((candidate) => candidate.instanceItemId === itemId);
      if (item) {
        const values = { ...getFormValues(itemId), [key]: value };
        setFieldErrors((prev) => {
          const next = { ...prev };
          item.sections.forEach((section) => section.fields.forEach((field) => {
            if (field.required && !isFieldRequired(item, field, values)) delete next[`${itemId}:${field.key}`];
          }));
          return next;
        });
      }
    }
    if (companionKeysByItem[itemId]?.[key]) forgetPayerText(itemId, key);
  };
  const setBirthdayPart = (itemId: string, key: string, part: keyof BirthdayParts, value: string) => {
    invalidate();
    const id = `${itemId}:${key}`;
    const parts = { ...(birthdayPartsRef.current[id] ?? birthdayPartsFromISO(formValuesRef.current[itemId]?.[key])), [part]: value };
    const nextParts = { ...birthdayPartsRef.current, [id]: parts };
    birthdayPartsRef.current = nextParts;
    setBirthdayParts(nextParts);
    const values = { ...(formValuesRef.current[itemId] ?? {}) };
    const parsed = parseBirthday(parts, getLocalToday());
    if (parsed.status === 'valid') values[key] = parsed.iso;
    else delete values[key];
    const next = { ...formValuesRef.current, [itemId]: values };
    formValuesRef.current = next;
    setFormValues(next);
    clearFieldMessages(itemId, key);
  };
  const pickPayer = (itemId: string, key: string, payer: FormPayer) => {
    const companion = companionKeysByItem[itemId]?.[key];
    if (!companion) return;
    forgetPayerText(itemId, key);
    invalidate();
    const stediId = payerCompanion(payer.stedi_id);
    setFormValues((prev) => {
      const next = withoutCompanion(itemId, key, { ...(prev[itemId] ?? {}), [key]: payer.display_name });
      return { ...prev, [itemId]: stediId ? { ...next, [companion]: stediId } : next };
    });
    clearFieldMessages(itemId, key);
  };
  // Payer search failed: every payer field is plain text for the rest of the
  // session, and the field it failed in keeps no companion.
  const fallBackToFreeText = (itemId: string, key: string) => {
    payerFallbackRef.current = true;
    setPayerFallback(true);
    setPayerConfirms({});
    invalidate();
    setFormValues((prev) => {
      const current = prev[itemId];
      if (!current) return prev;
      const next = withoutCompanion(itemId, key, current);
      return next === current ? prev : { ...prev, [itemId]: next };
    });
  };
  const setAgreementFieldValue = (itemId: string, idx: number, value: string) => {
    invalidate();
    setAgreementFieldValues((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] ?? {}), [idx]: value } }));
    setError('');
  };
  // What `payers` (the matches for payer field `key`'s unpicked `text`) mean: a
  // card-read name with one exact match is picked; other matches become an ask,
  // which it returns; no match settles the text. Rendered at once (flushSync), so a
  // handler that waited for it can go on from the render that has it.
  const payerTextIsCurrent = (itemId: string, key: string, text: string) => {
    const field = templateFieldsByItem[itemId]?.[key];
    const item = items.find((candidate) => candidate.instanceItemId === itemId);
    const values = formValuesRef.current[itemId] ?? {};
    return !!field && !!item && values[key] === text && isFieldVisible(field, values, item.sections);
  };
  const applyPayerResults = (itemId: string, key: string, text: string, payers: FormPayer[]): PayerConfirmView | null => {
    const id = `${itemId}:${key}`;
    if (!payerTextIsCurrent(itemId, key, text)) return null;
    const fromCard = autofilledRef.current[id] === text;
    const exact = fromCard ? exactPayerMatch(payers, text) : null;
    const ask: PayerConfirmView | null = !exact && payers.length > 0
      ? { source: fromCard ? 'card' : 'typed', text, options: payers }
      : null;
    flushSync(() => {
      if (exact) {
        pickPayer(itemId, key, exact);
        cardPickedRef.current[id] = true;
      } else if (ask) {
        setPayerConfirms((prev) => ({ ...prev, [id]: ask }));
      } else {
        payerSettledRef.current[id] = text;
      }
    });
    return ask;
  };
  // Search payer field `key`'s unpicked `text` as a pick would. A failure or the
  // server's fallback is the typeahead's own: plain text for the session.
  const searchPayerText = (itemId: string, key: string, text: string): Promise<PayerConfirmView | null> => {
    const query = payerQuery(text);
    if (!query || payerFallbackRef.current) return Promise.resolve(null);
    const id = `${itemId}:${key}`;
    const inflight = payerInflightRef.current[id];
    if (inflight && inflight.seq === payerSearchRef.current[id] && inflight.text === text) return inflight.promise;
    const seq = (payerSearchRef.current[id] ?? 0) + 1;
    payerSearchRef.current[id] = seq;
    const current = () => payerSearchRef.current[id] === seq && !payerFallbackRef.current && payerTextIsCurrent(itemId, key, text);
    const promise = searchFormPackageV2Payers(clientId, token, query).then(
      (res) => {
        if (!current()) return null;
        if (!res.fallback_to_free_text) return applyPayerResults(itemId, key, text, res.payers);
        flushSync(() => fallBackToFreeText(itemId, key));
        return null;
      },
      () => {
        if (current()) flushSync(() => fallBackToFreeText(itemId, key));
        return null;
      },
    );
    payerInflightRef.current[id] = { seq, text, promise };
    void promise.then(() => {
      if (payerInflightRef.current[id]?.promise === promise) delete payerInflightRef.current[id];
    });
    return promise;
  };
  const applyAutoFill = (itemId: string, mapping: Record<string, string>) => {
    const allowed = templateFieldsByItem[itemId] ?? {};
    const item = items.find((candidate) => candidate.instanceItemId === itemId);
    const values = formValuesRef.current[itemId] ?? {};
    Object.entries(mapping).forEach(([k, v]) => {
      if (!allowed[k] || !item || !isFieldVisible(allowed[k], values, item.sections)
          || !isFieldVisible(allowed[k], formValuesRef.current[itemId] ?? {}, item.sections)) return;
      const companion = companionKeysByItem[itemId]?.[k];
      const id = `${itemId}:${k}`;
      // The patient's own pick is better than a card read: it stays. A pick made
      // from an earlier card read gives way to the new card.
      if (companion && values[companion] && !cardPickedRef.current[id]) return;
      setFieldValue(itemId, k, v);
      // Text the patient already said isn't listed (or that matched nothing) stays settled.
      if (companion && typeof v === 'string' && payerSettledRef.current[id] !== v) {
        autofilledRef.current[id] = v;
        void searchPayerText(itemId, k, v);
      }
    });
  };
  // "My insurance isn't listed": the text stays, with no payer ID, and is not asked about again.
  const keepPayerText = (itemId: string, key: string) => {
    const id = `${itemId}:${key}`;
    const ask = payerConfirms[id];
    if (ask) payerSettledRef.current[id] = ask.text;
    dropPayerConfirms([id]);
  };
  /** The asks on an item's fields, by field key. */
  const payerConfirmsFor = (itemId: string): Record<string, PayerConfirmView> => {
    const out: Record<string, PayerConfirmView> = {};
    Object.keys(payerConfirms).forEach((id) => {
      if (id.startsWith(`${itemId}:`)) out[id.slice(itemId.length + 1)] = payerConfirms[id];
    });
    return out;
  };

  // Validation
  function fieldError(item: V2Item, field: IntakeFormField): string | null {
    if (field.field_type === 'static_text') return null;
    // A form-builder agreement the patient declined: its fields are not required.
    if (item.kind === 'agreement' && !agreedItemIds.includes(item.instanceItemId)) return null;
    const itemId = item.instanceItemId;
    // A conditionally-hidden field (its show_if is false) is never required.
    if (!isFieldVisible(field, getFormValues(itemId), item.sections)) return null;
    if (isBirthdayField(field)) {
      const parts = birthdayPartsRef.current[`${itemId}:${field.key}`] ?? birthdayPartsFromISO(getFormValues(itemId)[field.key]);
      const parsed = parseBirthday(parts, getLocalToday());
      switch (parsed.status) {
        case 'empty': return isFieldRequired(item, field, getFormValues(itemId)) ? `"${field.label}" is required.` : null;
        case 'partial': return `Enter the month, day and year for "${field.label}".`;
        case 'invalid': return `"${field.label}" must be a real date.`;
        case 'future': return `"${field.label}" can't be in the future.`;
        case 'valid': return null;
      }
    }
    if (!isFieldRequired(item, field, getFormValues(itemId))) return null;
    const val = getFormValues(itemId)[field.key];

    if (field.field_type === 'signature' || field.field_type === 'initials') {
      const canvas = intakeSigRefs.current.get(`${itemId}:${field.key}`);
      const saved = uploads.uploadsRef.current[slotKeys.fieldSig(itemId, field.key)];
      if (isCanvasEmpty(canvas) && !saved) return `Please complete "${field.label}".`;
    } else if (field.field_type === 'file_upload') {
      if (!uploads.uploadsRef.current[slotKeys.fieldFile(itemId, field.key)]) return `Please upload "${field.label}".`;
    } else if (field.field_type === 'checkbox') {
      if (field.must_be_true) return val === true ? null : `"${field.label}" must be checked.`;
      if (typeof val !== 'boolean') {
        return `Please answer "${field.label}".`;
      }
    } else if (field.field_type === 'multiselect') {
      if (!Array.isArray(val) || val.length === 0) return `Please select at least one option for "${field.label}".`;
    } else {
      if (val === null || val === undefined || val === '' || (typeof val === 'string' && !val.trim())) return `"${field.label}" is required.`;
    }
    return null;
  }

  function validateStep(index: number): boolean {
    const step = steps[index];

    if (step.type === 'agreement_review' && step.item?.kind === 'agreement') {
      if (step.item.required && !agreedItemIds.includes(step.item.instanceItemId)) {
        setError('Please agree to this document to continue.');
        return false;
      }
    }

    if (step.type === 'agreement_sig_field' && step.sigField && step.item) {
      const itemId = step.item.instanceItemId;
      if (!agreedItemIds.includes(itemId)) return true; // optional agreement left unaccepted
      const val = agreementFieldValues[itemId]?.[step.sigFieldIndex!] ?? '';
      if (step.sigField.required) {
        if (step.sigField.type === 'signature') {
          const canvas = agreementSigRefs.current.get(`${itemId}:${step.sigFieldIndex}`);
          const saved = uploads.uploadsRef.current[slotKeys.agreementSig(itemId, step.sigFieldIndex!)];
          if (isCanvasEmpty(canvas) && !saved) {
            setError(`Please sign "${step.sigField.label}".`);
            return false;
          }
        } else if (!val.trim()) {
          setError(`Please fill in "${step.sigField.label}".`);
          return false;
        }
      }
    }

    if (step.type === 'section' && step.item && step.fields) {
      const itemId = step.item.instanceItemId;
      const next: Record<string, string> = {};
      for (const field of step.fields) {
        const msg = fieldError(step.item, field);
        if (msg) next[`${itemId}:${field.key}`] = msg;
      }
      if (Object.keys(next).length > 0) {
        setFieldErrors(next);
        const first = step.fields.find((f) => next[`${itemId}:${f.key}`]);
        if (first) focusField(itemId, first.key);
        return false;
      }
    }

    if (step.type === 'consent' && consentRequired) {
      if (!consentName.trim()) { setError('Please type your name as a signature.'); return false; }
      if (isCanvasEmpty(finalSigRef.current) && !uploads.uploadsRef.current[slotKeys.consent]) {
        setError('Please draw your signature to consent.');
        return false;
      }
    }

    return true;
  }

  function reportUploadFailure(err: unknown) {
    setError(uploadErrorMessage(err));
    if (err instanceof V2UploadError && (err.kind === 'expired' || err.kind === 'unavailable')) {
      onLinkInvalid?.(err);
    }
  }

  // The live insurance check on Next (after validation and staging). Runs each
  // slot whose details this screen holds, when the DOB proof is present, the
  // request builds, its answers changed since the last check, and the attempt gate
  // allows it. Waits up to INSURANCE_CHECK_WAIT_MS; a later answer is recorded
  // silently. Returns true when an advisory outcome keeps the patient here.
  async function runInsuranceChecks(step: Step, retrySlot?: PolicySlot): Promise<boolean> {
    if (!dobProofToken || step.type !== 'section' || !step.item || !step.fields) return false;
    const itemId = step.item.instanceItemId;
    const screen = { itemId, fieldKeys: step.fields.map((f) => f.key) };
    type Settled = { outcome: InsuranceCheckOutcome | 'error'; reason: InsuranceUnavailableReason | null } | 'timeout';
    const runs: { plan: SlotCheckPlan; fingerprint: string; wasTemporary: boolean; result: Promise<Settled> }[] = [];
    for (const slot of insuranceSlots) {
      if (retrySlot && slot.slot !== retrySlot) continue;
      const plan = planSlotCheck(slot, items, formValues);
      const fingerprint = plan.fingerprint;
      if (!plan.request || !fingerprint || !screenTouchesSlot(plan, screen)) continue;
      const ledger = insuranceLedger[slot.slot];
      if (!canSendCheck(ledger, plan.request, retrySlot ? 'retry' : 'next')) continue;
      const wasTemporary = isTemporaryFailure(ledger);
      recordCheckSent(ledger, plan.request);
      const result = checkFormPackageV2Insurance(clientId, dobProofToken, plan.request).then(
        (res): Settled => {
          const reason = res.unavailable_reason ?? null;
          recordCheckResult(ledger, fingerprint, res.outcome, res.check_id, reason);
          return { outcome: res.outcome, reason };
        },
        (err: ApiError): Settled => {
          const reason = err.status === undefined ? 'payer_unavailable' : null;
          recordCheckResult(ledger, fingerprint, 'error', null, reason);
          return { outcome: 'error', reason };
        },
      );
      runs.push({ plan, fingerprint, wasTemporary, result });
    }
    if (runs.length === 0) return false;

    setChecking(true);
    const outcomes = await settleWithin(runs.map((run) => run.result), INSURANCE_CHECK_WAIT_MS, 'timeout' as const);

    // The fields were read-only during the wait, so `formValues` still matches the screen.
    const values = getFormValues(itemId);
    const visibleKeys = step.fields.filter((f) => isFieldVisible(f, values, step.item!.sections)).map((f) => f.key);
    const onScreen = (ref: FieldRef): boolean => ref.itemId === itemId && visibleKeys.indexOf(ref.key) !== -1;
    // Fields that are not on this screen are named with where they are instead.
    const stepIndex = steps.indexOf(step);
    const placementOf = (refs: FieldRef[]): FieldPlacement => {
      const at = refs
        .map((ref) => steps.findIndex((st) => st.type === 'section' && st.item?.instanceItemId === ref.itemId
          && !!st.fields?.some((f) => f.key === ref.key)))
        .filter((i) => i !== -1);
      return at.length > 0 && at.every((i) => i > stepIndex) ? 'later' : 'earlier';
    };
    const notices: InsuranceNotice[] = [];
    const highlights: Record<string, string> = {};
    runs.forEach(({ plan, fingerprint, wasTemporary }, i) => {
      const settled = outcomes[i];
      const outcome = settled === 'timeout' ? 'timeout' : settled.outcome;
      const ledger = insuranceLedger[plan.slot];
      // A retry that exhausts the wait or hits the server cap still explains the
      // previous temporary failure. A timeout leaves the real ledger pending.
      const exhausted = retrySlot && wasTemporary && (outcome === 'timeout' || outcome === 'limit_reached');
      const advisory = exhausted
        ? unavailableNotice({ ...ledger, lastOutcome: 'unavailable', lastUnavailableReason: 'payer_unavailable' })
        : settled === 'timeout' ? null
        : unavailableNotice({ ...ledger, lastOutcome: settled.outcome, lastUnavailableReason: settled.reason });
      if (advisory) {
        notices.push({ slot: plan.slot, stepId: step.id, fingerprint, ...advisory,
          temporary: exhausted || isTemporaryFailure(ledger) });
      }
      if (outcome === 'active') pendingConfirmationsRef.current.push(coverageConfirmedText(plan.companyName));
      if (!isFixableOutcome(outcome)) return;
      const about = outcome === 'check_member_id' ? (plan.memberIdField ? [plan.memberIdField] : [])
        : outcome === 'check_name_or_dob' ? plan.nameAndDobFields
        : [];
      const flagged = about.filter(onScreen).sort((a, b) => visibleKeys.indexOf(a.key) - visibleKeys.indexOf(b.key));
      const message = fixableMessage(outcome, flagged.length ? 'here' : about.length ? placementOf(about) : 'none', plan.policyholder);
      // On this screen the message goes under the first flagged field and the rest
      // are outlined only; otherwise it sits by the button.
      flagged.forEach((ref, idx) => { highlights[`${ref.itemId}:${ref.key}`] = idx === 0 ? message : ''; });
      notices.push({ slot: plan.slot, stepId: step.id, fingerprint, text: flagged.length ? null : message });
    });
    if (notices.length === 0 && !retrySlot) {
      setChecking(false);
      return false;
    }

    // Re-enable the fields before focusing the flagged one.
    flushSync(() => {
      setChecking(false);
      setInsuranceNotices((previous) => retrySlot
        ? previous.flatMap((n) => n.slot === retrySlot ? notices : [n]) : notices);
      setFieldHighlights((previous) => retrySlot ? { ...previous, ...highlights } : highlights);
    });
    const firstKey = visibleKeys.find((k) => `${itemId}:${k}` in highlights);
    if (firstKey) focusField(itemId, firstKey);
    return notices.length > 0;
  }

  async function retryInsuranceCheck(slotName: PolicySlot) {
    if (!dobProofToken || staging || checking || loading || isSubmitting || navLockRef.current) return;
    const notice = insuranceNotices.find((n) => n.slot === slotName && n.stepId === currentStep.id && n.retry);
    const slot = insuranceSlots.find((s) => s.slot === slotName);
    if (!notice || !slot || planSlotCheck(slot, items, formValues).fingerprint !== notice.fingerprint) return;
    await runInsuranceChecks(currentStep, slotName);
  }

  // Payer fields on `step` whose text still needs a look: visible, searchable, not a
  // pick, and not settled for this text. None once payer search has fallen back.
  function unsettledPayerFields(step: Step): PayerFieldRef[] {
    if (payerFallbackRef.current || step.type !== 'section' || !step.item || !step.fields) return [];
    const itemId = step.item.instanceItemId;
    const values = getFormValues(itemId);
    const companions = companionKeysByItem[itemId] ?? {};
    const out: PayerFieldRef[] = [];
    step.fields.forEach((field) => {
      const id = `${itemId}:${field.key}`;
      const companion = companions[field.key];
      const text = values[field.key];
      if (!companion || !isFieldVisible(field, values, step.item!.sections) || typeof text !== 'string' || !payerQuery(text)) return;
      if (values[companion] || payerSettledRef.current[id] === text) return;
      out.push({ id, itemId, key: field.key, text });
    });
    return out;
  }

  // The fields' asks, keyed by field id: the ones already showing, then the
  // typeahead's latest answer for the same text, then a search (waiting up to
  // PAYER_SEARCH_WAIT_MS). `looked`: answers may have changed (a pick, a fallback).
  async function payerAsksFor(fields: PayerFieldRef[]): Promise<{ asks: Record<string, PayerConfirmView>; looked: boolean }> {
    const asks: Record<string, PayerConfirmView> = {};
    const toSearch: PayerFieldRef[] = [];
    let looked = false;
    fields.forEach((field) => {
      const ask = payerConfirms[field.id];
      const typed = payerResultsRef.current[field.id];
      if (ask && ask.text === field.text) {
        asks[field.id] = ask;
      } else if (typed && typed.query === payerQuery(field.text)) {
        looked = true;
        const found = applyPayerResults(field.itemId, field.key, field.text, typed.payers);
        if (found) asks[field.id] = found;
      } else {
        toSearch.push(field);
      }
    });
    if (toSearch.length > 0) {
      looked = true;
      const found = await settleWithin(toSearch.map((field) => searchPayerText(field.itemId, field.key, field.text)), PAYER_SEARCH_WAIT_MS, LATE);
      toSearch.forEach((field, i) => {
        const ask = found[i];
        // Too late: Next or submit goes on without it, and its answer is dropped so
        // it cannot change the answers afterwards (the next Next or submit asks again).
        if (ask === LATE) forgetPayerSearch(field.id);
        else if (ask) asks[field.id] = ask;
      });
    }
    // A fallback during the wait dropped every ask.
    return { asks: payerFallbackRef.current ? {} : asks, looked };
  }

  // Next waits for the screen's unpicked payer text. 'hold': an ask keeps the
  // patient here, brought into view, until they choose or say it isn't listed.
  // 'again': answers may have changed while it looked, so Next starts over from the
  // render that has them. The fields are read-only meanwhile.
  async function checkPayersOnNext(step: Step): Promise<'hold' | 'again' | 'go'> {
    const fields = unsettledPayerFields(step);
    if (fields.length === 0) return 'go';
    if (fields.some((field) => payerConfirms[field.id]?.text !== field.text)) setChecking(true);
    let result: { asks: Record<string, PayerConfirmView>; looked: boolean };
    try {
      result = await payerAsksFor(fields);
    } finally {
      flushSync(() => setChecking(false));
    }
    const first = fields.find((field) => result.asks[field.id]);
    if (!first) return result.looked ? 'again' : 'go';
    flushSync(() => setPayerHoldStepId(step.id));
    focusField(first.itemId, first.key, PAYER_ASK);
    return 'hold';
  }

  // Submit gives unpicked payer text anywhere the same look (a resumed draft, a card
  // read on a screen already passed, "Save & return" past the insurance screen).
  // Returns the first screen with an ask, or null.
  async function checkPayersOnSubmit(): Promise<{ index: number; field: PayerFieldRef } | null> {
    const perStep = steps.map((step) => unsettledPayerFields(step));
    const { asks } = await payerAsksFor(([] as PayerFieldRef[]).concat(...perStep));
    for (let index = 0; index < steps.length; index += 1) {
      const field = perStep[index].find((f) => asks[f.id]);
      if (field) return { index, field };
    }
    return null;
  }

  // Leaving a screen: its insurance messages go, and any confirmations waiting
  // for an advance are shown on `targetStepId` (or dropped when going back).
  function resetInsuranceUi(targetStepId: string | null) {
    setFieldHighlights({});
    setInsuranceNotices([]);
    setPayerHoldStepId(null);
    const lines = pendingConfirmationsRef.current;
    pendingConfirmationsRef.current = [];
    setCoverageNote(targetStepId && lines.length ? { stepId: targetStepId, lines } : null);
  }

  // Stage the step's drawing (if any) before moving on. Nothing is kept unless the
  // upload succeeds, so a later Next with an empty canvas fails validation instead
  // of advancing without a signature.
  // `payersChecked`: a re-run after the payer look below, which does not look again.
  async function runNext(payersChecked: boolean) {
    if (staging || checking || navLockRef.current) return;
    if (!validateStep(currentStepIndex)) return;
    const step = steps[currentStepIndex];
    // Only a screen with unpicked payer text waits here. Any other goes on within
    // the tap, so its busy flags render before a second tap can arrive.
    if (!payersChecked && unsettledPayerFields(step).length > 0) {
      const payers = await checkPayersOnNext(step);
      if (payers === 'hold') return;
      if (payers === 'again') {
        void rerunRef.current.next(true);
        return;
      }
    }

    setStaging(true);
    try {
      if (step.type === 'agreement_sig_field' && step.item && step.sigField?.type === 'signature'
          && agreedItemIds.includes(step.item.instanceItemId)) {
        const itemId = step.item.instanceItemId;
        const canvas = agreementSigRefs.current.get(`${itemId}:${step.sigFieldIndex}`);
        if (!isCanvasEmpty(canvas)) {
          await uploads.stageSignature(slotKeys.agreementSig(itemId, step.sigFieldIndex!), canvas);
          invalidate();
        }
      }
      if (step.type === 'section' && step.item && step.fields
          && !(step.item.kind === 'agreement' && !agreedItemIds.includes(step.item.instanceItemId))) {
        const itemId = step.item.instanceItemId;
        for (const field of step.fields) {
          if (!isFieldVisible(field, getFormValues(itemId), step.item.sections)) continue;
          if (field.field_type !== 'signature' && field.field_type !== 'initials') continue;
          const canvas = intakeSigRefs.current.get(`${itemId}:${field.key}`);
          if (!isCanvasEmpty(canvas)) {
            await uploads.stageSignature(slotKeys.fieldSig(itemId, field.key), canvas);
            invalidate();
          }
        }
      }
      if (step.type === 'consent' && !isCanvasEmpty(finalSigRef.current)) {
        await uploads.stageSignature(slotKeys.consent, finalSigRef.current);
        invalidate();
      }
    } catch (err) {
      reportUploadFailure(err);
      setStaging(false);
      return;
    }
    setStaging(false);

    setInsuranceNotices([]);
    setFieldHighlights({});
    if (await runInsuranceChecks(step)) return;

    if (editingFromReview) {
      setEditingFromReview(false);
      navLockRef.current = true;
      setDirection(1);
      setCurrentStepIndex(steps.length - 1);
      setError('');
      setFieldErrors({});
      resetInsuranceUi(steps[steps.length - 1].id);
      return;
    }
    if (currentStepIndex < steps.length - 1) {
      navLockRef.current = true;
      setDirection(1);
      const nextIndex = visibleIndexes[visiblePosition + 1];
      setCurrentStepIndex(nextIndex);
      setError('');
      setFieldErrors({});
      resetInsuranceUi(steps[nextIndex].id);
    }
  }

  // From the review back to `stepIndex`, in "Save & return" mode.
  function backFromReview(stepIndex: number) {
    setEditingFromReview(true);
    navLockRef.current = true;
    setDirection(-1);
    setCurrentStepIndex(stepIndex);
    setError('');
    setFieldErrors({});
    resetInsuranceUi(null);
  }

  function handleNext() {
    return runNext(false);
  }

  function handleEdit(stepIndex: number) {
    if (staging || checking || navLockRef.current) return;
    backFromReview(stepIndex);
  }

  function handleBack() {
    if (staging || checking || navLockRef.current) return;
    setEditingFromReview(false);
    if (currentStepIndex > 0) {
      navLockRef.current = true;
      setDirection(-1);
      setCurrentStepIndex(visibleIndexes[visiblePosition - 1]);
      setError('');
      setFieldErrors({});
      resetInsuranceUi(null);
    }
  }

  // `payersChecked`: a re-run after the payer look below, which does not look again.
  async function runSubmit(payersChecked: boolean) {
    for (let i = 0; i < steps.length; i++) {
      if (!validateStep(i)) {
        setCurrentStepIndex(i);
        return;
      }
    }

    setError('');
    if (!payersChecked && !payloadRef.current && steps.some((step) => unsettledPayerFields(step).length > 0)) {
      // Nothing is submitted yet: the button says what it is doing, and Back and
      // Edit wait too, since the look is about these answers.
      setChecking(true);
      let ask: Awaited<ReturnType<typeof checkPayersOnSubmit>>;
      try {
        ask = await checkPayersOnSubmit();
      } finally {
        flushSync(() => setChecking(false));
      }
      if (ask) {
        // Back to the ask; Next from there returns to the review.
        const { index, field } = ask;
        payerFocusRef.current = { stepId: steps[index].id, itemId: field.itemId, key: field.key };
        backFromReview(index);
        setPayerHoldStepId(steps[index].id);
        return;
      }
      // Answers may have changed while it looked (a pick, a fallback): submit from
      // the render that has them.
      void rerunRef.current.submit(true);
      return;
    }
    setIsSubmitting(true);
    try {
      // The insurance references are resolved here, once, with the payload (#48).
      // The request hash covers them, so a retry must repeat them byte for byte:
      // - A retry in this page replays payloadRef, so a check_id that arrives late
      //   (a timed-out check) never changes a payload already built; only an edit
      //   (invalidate) rebuilds it, against the edited answers.
      // - The 409 re-sign remount rebuilds from the page's ledger. Its references
      //   can differ only by such a late check_id, which is harmless: the refused
      //   attempt was never accepted, so no stored hash can conflict.
      // - After a refresh the ledger and the proof are gone and the key is left
      //   out. That conflicts with an earlier attempt only if the server accepted
      //   it, and then the package is used up: the page shows "already submitted"
      //   on the 410, or on a 409 idempotency_conflict.
      if (!payloadRef.current) {
        payloadRef.current = buildV2SubmitPayload(pkg, uploads.idempotencyKey, {
          agreedItemIds,
          agreementFieldValues,
          formValues,
          uploads: uploads.uploadsRef.current,
          consentName,
          consentRequired,
          insuranceChecks: submittableInsuranceChecks(insuranceSlots, items, formValues, insuranceLedger),
        });
      }
      await onSubmit(payloadRef.current);
    } catch (err: any) {
      if (err instanceof V2AssemblyError) payloadRef.current = null;
      setError(err?.message || 'Submission failed.');
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleSubmit() {
    return runSubmit(false);
  }

  // Files stage the moment they are picked; the renderer owns link-death handling
  // and payload invalidation so the section screen stays presentational.
  async function stageFileForField(itemId: string, fieldKey: FileUploadFieldKey, file: File) {
    const slot = slotKeys.fieldFile(itemId, fieldKey);
    try {
      const res = await uploads.stageFile(slot, fieldKey, file);
      invalidate();
      return { value: res.upload_id as unknown, fieldMapping: res.field_mapping };
    } catch (err) {
      if (err instanceof V2UploadError && (err.kind === 'expired' || err.kind === 'unavailable')) onLinkInvalid?.(err);
      throw err;
    }
  }

  // Keyboard: Enter advances single-input steps (never review, never mid-upload).
  // On a multi-field section screen the software keyboard's return key moves
  // focus to the next field instead — submitting eight fields from the first
  // input was a surprise, not a shortcut.
  const handleNextRef = useRef(handleNext);
  handleNextRef.current = handleNext;
  // The latest render's Next and submit, for a re-run after a payer look.
  const rerunRef = useRef({ next: runNext, submit: runSubmit });
  rerunRef.current = { next: runNext, submit: runSubmit };
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Enter') {
        if (currentStep.type === 'review') return;
        const target = e.target as HTMLElement;
        // A focused button (a payer choice, Back) acts on its own Enter.
        if (target.tagName === 'TEXTAREA' || target.tagName === 'SELECT' || target.tagName === 'BUTTON') return;
        if (currentStep.type === 'section' && target.tagName === 'INPUT') {
          e.preventDefault();
          const root = document.querySelector('[data-step-id]');
          const focusables = root ? Array.from(root.querySelectorAll<HTMLElement>('input, select, textarea')) : [];
          const idx = focusables.indexOf(target);
          if (idx >= 0 && idx < focusables.length - 1) focusables[idx + 1].focus();
          else target.blur();
          return;
        }
        e.preventDefault();
        void handleNextRef.current();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [currentStep.type]);

  // Keep drawn ink aligned through width changes (rotation): re-rasterize the
  // current step's canvases when their element size changes.
  useEffect(() => {
    const detach = attachCanvasPreserve([
      ...Array.from(agreementSigRefs.current.values()),
      ...Array.from(intakeSigRefs.current.values()),
      finalSigRef.current,
    ]);
    return detach;
  }, [currentStepIndex]);

  // Submit went back to an ask: focus it once its screen has settled.
  useEffect(() => {
    const pending = payerFocusRef.current;
    if (!pending || pending.stepId !== settledStepId) return;
    payerFocusRef.current = null;
    focusField(pending.itemId, pending.key, PAYER_ASK);
  }, [settledStepId]);

  // One page scroll: every step starts at the top (instant — the enter
  // animation provides the motion).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStepIndex]);

  // No pull-to-refresh / rubber-band chaining while a form is in progress.
  useEffect(() => {
    document.body.classList.add('v2-flow-active');
    return () => document.body.classList.remove('v2-flow-active');
  }, []);

  // Draft resume (server-side, token-keyed — never browser storage, so no PHI
  // lands on the device). On mount, read any saved draft for this link and
  // restore the flow where the patient left off; uploads come back by id (their
  // rows still live server-side), though a signature's preview is blank until
  // redrawn. A missing draft or any read failure fails open to a fresh start.
  useEffect(() => {
    let cancelled = false;
    getFormPackageV2Draft(clientId, token)
      .then(({ draft }) => {
        if (cancelled || !draft) return;
        setAgreedItemIds(Array.isArray(draft.agreedItemIds) ? draft.agreedItemIds : []);
        setAgreementFieldValues(draft.agreementFieldValues ?? {});
        const restored = { ...draft.formValues };
        const restoredParts: Record<string, BirthdayParts> = {};
        items.forEach((item) => {
          if (!restored[item.instanceItemId]) return;
          const values = { ...restored[item.instanceItemId] };
          item.sections.forEach((section) => section.fields.filter(isBirthdayField).forEach((field) => {
            const parts = birthdayPartsFromISO(values[field.key]);
            restoredParts[`${item.instanceItemId}:${field.key}`] = parts;
            const parsed = parseBirthday(parts, getLocalToday());
            if (parsed.status === 'valid') values[field.key] = parsed.iso;
            else delete values[field.key];
          }));
          restored[item.instanceItemId] = values;
        });
        birthdayPartsRef.current = restoredParts;
        setBirthdayParts(restoredParts);
        formValuesRef.current = restored;
        setFormValues(restored);
        setConsentName(typeof draft.consentName === 'string' ? draft.consentName : '');
        if (draft.uploads && typeof draft.uploads === 'object') {
          uploads.hydrate(draft.uploads);
          // Rebuild file-field display metadata (filename) from the staged rows so
          // the section and review screens name the resumed uploads.
          const metaByItem: Record<string, Record<string, UploadMeta>> = {};
          Object.entries(draft.uploads).forEach(([slot, up]) => {
            const m = /^file:([^:]+):(.+)$/.exec(slot);
            if (m && up.fileName) {
              const [, itemId, fieldKey] = m;
              // No fileType/preview survives a draft; the review screen falls back
              // to the "Uploaded" caption, so an empty type is safe here.
              (metaByItem[itemId] ??= {})[fieldKey] = { fileName: up.fileName, fileType: '' };
            }
          });
          if (Object.keys(metaByItem).length) setUploadMetaByItem(metaByItem);
        }
        // steps is built from the package (not from answers), so its length is
        // stable; clamp a saved index defensively all the same.
        if (typeof draft.step === 'number') {
          setCurrentStepIndex(Math.max(0, Math.min(draft.step, steps.length - 1)));
        }
      })
      .catch(() => { /* fail open: start fresh */ })
      .finally(() => { if (!cancelled) setHydrating(false); });
    return () => { cancelled = true; };
    // Runs once per link; clientId/token identify the draft and don't change here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, token]);

  // "The patient has actually started" — the same signal that warns before an
  // unload. Autosave waits for this so a fresh form idling on the welcome screen
  // never writes an empty draft.
  const dirty = agreedItemIds.length > 0
    || consentName !== ''
    || Object.values(birthdayParts).some((parts) => parts.month !== '' || parts.day !== '' || parts.year !== '')
    || Object.keys(formValues).length > 0
    || Object.keys(agreementFieldValues).length > 0
    || Object.keys(uploads.uploads).length > 0;

  // Debounced autosave: persist a snapshot ~1s after the flow last changed (and
  // immediately-ish on each step advance, since currentStepIndex is a dep). Gated
  // on hydration (don't clobber the server draft before reading it), on having
  // real content (`dirty`), and paused during submit. Uploads carry by id only;
  // canvas refs and previews are never serialized.
  useEffect(() => {
    if (hydrating || isSubmitting || !dirty) return;
    const draftUploads: FormPackageV2Draft['uploads'] = {};
    Object.entries(uploads.uploads).forEach(([slot, up]) => {
      draftUploads[slot] = { uploadId: up.uploadId, uploadKind: up.uploadKind, byteSize: up.byteSize, fileName: up.fileName };
    });
    const draft: FormPackageV2Draft = {
      v: 1,
      step: currentStepIndex,
      agreedItemIds,
      agreementFieldValues,
      formValues,
      consentName,
      uploads: draftUploads,
    };
    const snapshot = JSON.stringify(draft);
    if (snapshot === lastSavedRef.current) return;
    const t = setTimeout(() => {
      lastSavedRef.current = snapshot;
      saveFormPackageV2Draft(clientId, token, draft).catch(() => {
        // A failed save must not block the patient; drop the marker so the next
        // change retries rather than assuming this snapshot persisted.
        lastSavedRef.current = null;
      });
    }, 1000);
    return () => clearTimeout(t);
  }, [hydrating, isSubmitting, dirty, currentStepIndex, agreedItemIds, agreementFieldValues, formValues, consentName, uploads.uploads, clientId, token]);

  // A refresh now restores the flow (draft above), but still warn before the tab
  // discards work that may not have hit the last debounced save.
  useEffect(() => {
    if (!dirty || isSubmitting) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty, isSubmitting]);

  const variants: Variants = reduceMotion
    ? {
        initial: { opacity: 0 },
        active: { opacity: 1, transition: { duration: 0.01 } },
        exit: { opacity: 0, transition: { duration: 0.01 } },
      }
    : {
        initial: (d: number) => ({ opacity: 0, x: d > 0 ? 24 : -24 }),
        active: { opacity: 1, x: 0, transition: { duration: 0.16, ease: 'easeOut' } },
        exit: (d: number) => ({ opacity: 0, x: d > 0 ? -24 : 24, transition: { duration: 0.08, ease: 'easeIn' } }),
      };

  const submitting = loading || isSubmitting;
  const primaryLabelByType: Record<StepType, string> = {
    welcome: 'Get Started',
    agreement_review: 'Confirm & Continue',
    agreement_sig_field: 'Next Step',
    section: 'Next',
    consent: 'Review All',
    review: 'Complete & Submit',
  };
  // An advisory insurance message is showing and its answers are unchanged: Next
  // would not re-check, so it says what it does. Planned only while one shows.
  const screenNotices = insuranceNotices.filter((n) => {
    if (n.stepId !== currentStep.id) return false;
    const slot = insuranceSlots.find((s) => s.slot === n.slot);
    return !n.temporary || (!!slot && planSlotCheck(slot, items, formValues).fingerprint === n.fingerprint);
  });
  const continueAnyway = screenNotices.some((n) => {
    const slot = insuranceSlots.find((s) => s.slot === n.slot);
    return !!slot && planSlotCheck(slot, items, formValues).fingerprint === n.fingerprint;
  });
  // Next or submit stopped here for an ask that is still open (#51).
  const payerAsking = payerHoldStepId === currentStep.id
    && unsettledPayerFields(currentStep).some((field) => payerConfirms[field.id]?.text === field.text);
  const primaryLabel = currentStep.type === 'review'
    ? (checking ? 'Checking your insurance…' : submitting ? 'Submitting...' : 'Complete & Submit')
    : staging ? 'Saving…'
    : checking ? 'Checking your insurance…'
    : continueAnyway ? 'Continue anyway'
    : editingFromReview ? 'Save & return'
    : primaryLabelByType[currentStep.type];
  const primaryDisabled = currentStep.type === 'review' ? submitting || checking : staging || checking;
  const handlePrimary = () => {
    if (currentStep.type === 'review') void handleSubmit();
    else void handleNext();
  };

  return (
    <div className="flex flex-1 flex-col">
      <V2ProgressHeader
        practiceInfo={practiceInfo}
        current={visiblePosition + 1}
        total={visibleIndexes.length}
        progress={progress}
        stageLabel={currentStep?.stageLabel}
        stagePos={currentStep?.stagePos}
        stageSize={currentStep?.stageSize}
        reducedMotion={!!reduceMotion}
      />

      <div className="flex-1 px-5 pt-5 pb-6 sm:px-0 sm:pt-4 sm:pb-0">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep.id}
            custom={direction}
            variants={variants}
            initial="initial"
            animate="active"
            exit="exit"
            className="space-y-6"
            data-step-id={currentStep.id}
            data-step-type={currentStep.type}
            data-settled={settledStepId === currentStep.id ? 'true' : 'false'}
            onAnimationComplete={(definition) => {
              if (definition === 'active') {
                navLockRef.current = false;
                setSettledStepId(currentStep.id);
              }
            }}
          >

            {coverageNote?.stepId === currentStep.id && <CoverageConfirmedNote lines={coverageNote.lines} />}

            {currentStep.type === 'welcome' && (
              <V2WelcomeStep items={items} />
            )}

            {currentStep.type === 'agreement_review' && currentStep.item?.kind === 'agreement' && (
              <V2AgreementReviewStep
                item={currentStep.item}
                agreed={agreedItemIds.includes(currentStep.item.instanceItemId)}
                onToggleAgree={(checked) => {
                  const id = currentStep.item!.instanceItemId;
                  invalidate();
                  setAgreedItemIds((prev) => checked ? [...prev, id] : prev.filter((x) => x !== id));
                  setError('');
                }}
              />
            )}

            {currentStep.type === 'agreement_sig_field' && currentStep.sigField && currentStep.item && (
              <V2AgreementSigStep
                item={currentStep.item}
                sigField={currentStep.sigField}
                sigFieldIndex={currentStep.sigFieldIndex!}
                value={agreementFieldValues[currentStep.item.instanceItemId]?.[currentStep.sigFieldIndex!]}
                onChange={(v) => setAgreementFieldValue(currentStep.item!.instanceItemId, currentStep.sigFieldIndex!, v)}
                sigRefs={agreementSigRefs}
                staged={uploads.uploads[slotKeys.agreementSig(currentStep.item.instanceItemId, currentStep.sigFieldIndex!)]}
              />
            )}

            {currentStep.type === 'section' && currentStep.item && currentStep.section && currentStep.fields && (
              <V2SectionStep
                item={currentStep.item}
                section={currentStep.section}
                fields={currentStep.fields}
                sectionIndex={currentStep.sectionIndex}
                sectionCount={currentStep.sectionCount}
                partIndex={currentStep.partIndex}
                partCount={currentStep.partCount}
                clientId={clientId}
                token={token}
                values={getFormValues(currentStep.item.instanceItemId)}
                onFieldChange={(key, v) => setFieldValue(currentStep.item!.instanceItemId, key, v)}
                birthdayParts={birthdayParts}
                onBirthdayPartChange={(key, part, value) => setBirthdayPart(currentStep.item!.instanceItemId, key, part, value)}
                fieldErrors={fieldErrors}
                fieldHighlights={fieldHighlights}
                disabled={checking}
                payerFallback={payerFallback}
                onPayerFallback={(key) => fallBackToFreeText(currentStep.item!.instanceItemId, key)}
                onPayerPick={(key, payer) => pickPayer(currentStep.item!.instanceItemId, key, payer)}
                payerConfirms={payerConfirmsFor(currentStep.item.instanceItemId)}
                onPayerNotListed={(key) => keepPayerText(currentStep.item!.instanceItemId, key)}
                onPayerResults={(key, query, payers) => { payerResultsRef.current[`${currentStep.item!.instanceItemId}:${key}`] = { query, payers }; }}
                uploadMeta={uploadMetaByItem[currentStep.item.instanceItemId] ?? {}}
                onUploadMetaChange={(key, meta) => {
                  const itemId = currentStep.item!.instanceItemId;
                  setUploadMetaByItem((prev) => ({ ...prev, [itemId]: { ...(prev[itemId] ?? {}), [key]: meta } }));
                }}
                onUploadMetaRemove={(key) => {
                  const itemId = currentStep.item!.instanceItemId;
                  uploads.dropSlot(slotKeys.fieldFile(itemId, key));
                  invalidate();
                  setUploadMetaByItem((prev) => { const next = { ...(prev[itemId] ?? {}) }; delete next[key]; return { ...prev, [itemId]: next }; });
                }}
                onAutoFill={(mapping) => applyAutoFill(currentStep.item!.instanceItemId, mapping)}
                stageFileForField={(fieldKey, file) => stageFileForField(currentStep.item!.instanceItemId, fieldKey, file)}
                sigRefs={intakeSigRefs}
                stagedUploads={uploads.uploads}
              />
            )}

            {currentStep.type === 'consent' && (
              <V2ConsentStep
                consentRequired={consentRequired}
                consentName={consentName}
                onNameChange={(v) => { invalidate(); setConsentName(v); }}
                finalSigRef={finalSigRef}
                onClearSig={() => finalSigRef.current?.clear()}
                staged={uploads.uploads[slotKeys.consent]}
              />
            )}

            {currentStep.type === 'review' && (
              <V2ReviewStep
                sections={buildReviewModel({
                  steps,
                  agreedItemIds,
                  agreementFieldValues,
                  formValues,
                  uploadMetaByItem,
                  uploads: uploads.uploads,
                  consentName,
                  consentRequired,
                })}
                onEdit={handleEdit}
              />
            )}

          </motion.div>
        </AnimatePresence>
      </div>

      <V2FooterBar
        onBack={handleBack}
        backDisabled={currentStepIndex === 0 || staging || checking}
        error={error}
        notices={[
          ...(payerAsking ? [{ text: PAYER_CONFIRM_NOTICE }] : []),
          ...screenNotices.filter((n) => !!n.text).map((n) => ({
            text: n.text!,
            ...(n.retry ? { action: {
              label: 'Try again',
              accessibleName: screenNotices.filter((notice) => notice.retry).length > 1
                ? `Try again for ${n.slot} insurance` : 'Try again',
              disabled: staging || checking || submitting,
              onClick: () => { void retryInsuranceCheck(n.slot); },
            } } : {}),
          })),
        ]}
        primaryLabel={primaryLabel}
        primaryDisabled={primaryDisabled}
        onPrimary={handlePrimary}
      />
    </div>
  );
}
